import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { ArrowRight, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SignalWaitDot } from "@/motion/SignalWaitDot";
import { signal } from "@/motion/signals";

import type {
  ChatEmbed,
  ChatPart,
  ChatTurn,
  ChatTurnResult,
  ChatWidgetPart,
  ThreadItem,
} from "./types";

const COMPOSER_MAX_PX = 384;

export function textTurns(items: ThreadItem[]): ChatTurn[] {
  return items.flatMap((item) =>
    item.type === "text" ? [{ role: item.role, text: item.text }] : [],
  );
}

export function threadParts(items: ThreadItem[]): ChatPart[] {
  return items.map((item) =>
    item.type === "text"
      ? { type: "text", role: item.role, text: item.text }
      : { ...item },
  );
}

export interface WebChatProps {
  composerId: string;
  placeholder: string;
  replyPlaceholder?: string;
  idleSubmitLabel: string;
  maxLength: number;
  postTurn: (
    messages: ChatPart[],
    handlers?: { onDelta?: (text: string) => void },
  ) => Promise<ChatTurnResult>;
  renderEmbed: (
    embed: ChatEmbed,
    ctx: { fieldId: string; onError: (error: string) => void },
  ) => ReactNode;
  onUnavailable: (firstUserText: string) => void;
  onEmptySubmit: () => void;
  footer?: ReactNode;
  beforeStart?: (setDraft: (text: string) => void) => ReactNode;
  onActiveChange?: (active: boolean) => void;
  onDismiss?: () => void;
  expanded?: boolean;
}

export function WebChat({
  composerId,
  placeholder,
  replyPlaceholder = "Reply to Chill",
  idleSubmitLabel,
  maxLength,
  postTurn,
  renderEmbed,
  onUnavailable,
  onEmptySubmit,
  footer,
  beforeStart,
  onActiveChange,
  onDismiss,
  expanded = false,
}: WebChatProps): ReactElement {
  const [message, setMessage] = useState("");
  const [items, setItems] = useState<ThreadItem[]>([]);
  const [thinking, setThinking] = useState(false);
  const [streamingReply, setStreamingReply] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [grown, setGrown] = useState(false);
  const thread = useRef<HTMLDivElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const node = thread.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [items.length, thinking, streamingReply]);

  useEffect(() => {
    const node = composer.current;
    if (!node) return;
    node.style.height = "auto";
    const content = node.scrollHeight;
    node.style.height = `${Math.min(content, COMPOSER_MAX_PX)}px`;
    setGrown(content > 48);
  }, [message]);

  async function sendCurrent(): Promise<void> {
    if (thinking) return;

    const text = message.trim().slice(0, maxLength);
    if (!text) {
      if (items.length === 0) onEmptySubmit();
      return;
    }

    const nextParts: ChatPart[] = [
      ...threadParts(items),
      { type: "text", role: "user", text },
    ];
    setItems(nextParts);
    onActiveChange?.(true);
    setMessage("");
    setError(null);
    setStreamingReply("");
    setThinking(true);

    const result = await postTurn(nextParts, {
      onDelta(text) {
        setThinking(false);
        setStreamingReply((current) => current + text);
      },
    });
    setThinking(false);
    setStreamingReply("");

    if (!result.ok) {
      const first = nextParts.find(
        (part): part is Extract<ChatPart, { type: "text" }> =>
          part.type === "text" && part.role === "user",
      );
      onUnavailable(first?.text ?? "");
      return;
    }

    setItems(applyTurnResult(nextParts, result));
  }

  function dismiss(): void {
    onActiveChange?.(false);
    onDismiss?.();
  }

  function onComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) {
      return;
    }
    event.preventDefault();
    void sendCurrent();
  }

  const started = items.length > 0;

  return (
    <div className="w-full">
      {started ? (
        <div
          ref={thread}
          aria-live="polite"
          aria-label="Conversation with Chill"
          className={cn(
            signal.arrive,
            "relative mb-3 space-y-3 overflow-y-auto rounded-[1.5rem] border border-white/80 bg-white/70 p-4 text-left shadow-[0_18px_50px_rgba(34,73,66,0.1)] backdrop-blur-xl",
            expanded
              ? "max-h-[min(70vh,36rem)] min-h-[16rem]"
              : "max-h-80",
            expanded && onDismiss ? "pr-12" : null,
          )}
          onClick={!expanded && onActiveChange ? () => onActiveChange(true) : undefined}
        >
          {expanded && onDismiss ? (
            <button
              type="button"
              onClick={dismiss}
              aria-label="Minimize conversation"
              className="absolute right-3 top-3 z-10 inline-flex size-8 items-center justify-center rounded-full text-[#65736f] transition-colors hover:bg-white/80 hover:text-[#20272b]"
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          ) : null}
          {items.map((item, index) =>
            item.type === "text" ? (
              <Bubble key={`${item.role}-${index}`} turn={item} />
            ) : item.dismissed ? null : (
              <div key={item.id}>
                {renderEmbed(item.embed, {
                  fieldId: `embed-${item.id}`,
                  onError: setError,
                })}
              </div>
            ),
          )}
          {streamingReply ? (
            <Bubble turn={{ role: "assistant", text: streamingReply }} />
          ) : null}
          {thinking ? (
            <div className="flex items-center gap-2 text-sm text-[#65736f]">
              <SignalWaitDot className="bg-[#3d8e85]" />
              <span className="sr-only">Chill is thinking</span>
            </div>
          ) : null}
        </div>
      ) : null}

      <form
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          void sendCurrent();
        }}
        className={cn(
          "flex flex-col gap-2 rounded-[1.5rem] border border-white/80 bg-white/90 p-2 shadow-[0_18px_50px_rgba(34,73,66,0.12)] backdrop-blur-xl sm:flex-row sm:pl-5 sm:pr-1.5",
          grown
            ? "sm:items-end sm:rounded-[1.5rem] sm:py-2"
            : "sm:items-center sm:rounded-full sm:py-1.5",
        )}
      >
        <label htmlFor={composerId} className="sr-only">
          Message to Chill
        </label>
        <div className="min-w-0 flex-1">
          <textarea
            ref={composer}
            id={composerId}
            rows={1}
            wrap="soft"
            value={message}
            onChange={(event) => setMessage(event.currentTarget.value)}
            onKeyDown={onComposerKeyDown}
            maxLength={maxLength}
            placeholder={started ? replyPlaceholder : placeholder}
            autoComplete="off"
            className="block max-h-96 min-h-11 w-full resize-none overflow-x-hidden overflow-y-auto whitespace-pre-wrap break-words bg-transparent px-3 py-2.5 text-base leading-6 text-[#15231f] outline-none [field-sizing:fixed] placeholder:text-[#8a9692] sm:px-1 sm:py-2"
          />
        </div>
        <Button
          type="submit"
          size="lg"
          disabled={thinking}
          className="h-11 shrink-0 rounded-full bg-[#dceffc] px-5 text-sm text-[#20272b] shadow-none hover:bg-[#c8e6fa]"
        >
          {thinking ? (
            <SignalWaitDot className="bg-white" />
          ) : (
            <>
              {started ? "Send" : idleSubmitLabel}
              <ArrowRight aria-hidden="true" />
            </>
          )}
        </Button>
      </form>

      {error ? (
        <p className="mt-3 text-left text-sm text-[#8a4b2f]">{error}</p>
      ) : null}

      {!started ? beforeStart?.(setMessage) : null}
      {started ? footer : null}
    </div>
  );
}

export function applyTurnResult(
  parts: ChatPart[],
  result: Extract<ChatTurnResult, { ok: true }>,
): ChatPart[] {
  const next = [...parts];
  if (result.reply) {
    next.push({ type: "text", role: "assistant", text: result.reply });
  }
  const incoming = result.widgets?.length
    ? result.widgets
    : result.embed
      ? [widgetFromEmbed(result.embed)]
      : [];
  for (const widget of incoming) {
    const index = next.findIndex(
      (part) => part.type === "widget" && part.id === widget.id,
    );
    if (index >= 0) next[index] = widget;
    else next.push(widget);
  }
  return next;
}

function widgetFromEmbed(embed: ChatEmbed): ChatWidgetPart {
  const id =
    typeof embed.id === "string" && embed.id.trim()
      ? embed.id.trim()
      : `widget-${embed.kind}`;
  return {
    type: "widget",
    id,
    tool: "present_intake",
    input: {},
    embed,
  };
}

function Bubble({ turn }: { turn: ChatTurn }): ReactElement {
  const isOwner = turn.role === "user";
  return (
    <div className={cn("flex", isOwner ? "justify-end" : "justify-start")}>
      <p
        className={cn(
          signal.arrive,
          "max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm leading-6",
          isOwner
            ? "bg-[#dceffc] text-[#20272b]"
            : "bg-[#eef4f1] text-[#22322e]",
        )}
      >
        {turn.text}
      </p>
    </div>
  );
}
